import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useApi from '../api';
import useAnalytics from '../analytics/analytics';
import BeatLoader from '../ui/BeatLoader';
import { formatDate, parseApiDate } from '../utils/dateFormatter';
import { ModerationBadge } from './SessionModeration';

import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Select } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Bot, ChevronLeft, Code, Headset, Mail, SlidersHorizontal, Sparkles, X, Globe, Monitor } from 'lucide-react';
import {
    getIdentityVerificationTitle,
    resolveIdentityStatus,
} from '../../lib/visitorIdentity';
import VisitorIdentitySummary from '../identity/VisitorIdentitySummary';

interface SessionsCardProps {
    setSessionId: (id: string) => void;
    selectedSessionId: string | null;
    selectedSessionStatus: string;
    setSelectedSessionStatus: (status: string) => void;
    orgEvent?: {
        type?: string;
        [key: string]: any;
    } | null;
    onNavigateHome?: () => void;
}

const SessionsCard: React.FC<SessionsCardProps> = ({
    setSessionId,
    selectedSessionId,
    selectedSessionStatus,
    setSelectedSessionStatus,
    orgEvent,
    onNavigateHome,
}) => {
    const [loading, setLoading] = useState(true);
    const [sessions, setSessions] = useState<any[]>([]);
    const [searchInput, setSearchInput] = useState('');
    const [page, setPage] = useState(1);
    const [timeFilter, setTimeFilter] = useState('last_90_days');
    const [sortOption, setSortOption] = useState('last_desc');
    const [agentType, setAgentType] = useState<string | null>(null);
    const [agentFilter, setAgentFilter] = useState<string>('all');
    const [sourceFilter, setSourceFilter] = useState<string>('all');
    const [allAgents, setAllAgents] = useState<Array<{ id: string; parent_agent_id: string; name: string }>>([]);
    const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

    const [totalPages, setTotalPages] = useState(1);
    const navigate = useNavigate();
    const location = useLocation();
    const { get } = useApi();
    const { captureEvent } = useAnalytics();
    const realtimeRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
    const pageRef = useRef(page);
    const searchInputRef = useRef(searchInput);
    const timeFilterRef = useRef(timeFilter);
    const sortOptionRef = useRef(sortOption);
    type GetSessionsOptions = {
        silent?: boolean;
    };
    const getSessionsRef = useRef<
        (page?: number, search?: string, timeFilterValue?: string, sortValue?: string, agentFilterValue?: string, sourceFilterValue?: string, options?: GetSessionsOptions) => Promise<void>
    >(async () => { });

    const typeMap: { [key: string]: string } = {
        'operator': 'product',
        'sales': 'website'
    };

    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    useEffect(() => {
        searchInputRef.current = searchInput;
    }, [searchInput]);

    useEffect(() => {
        timeFilterRef.current = timeFilter;
    }, [timeFilter]);

    useEffect(() => {
        sortOptionRef.current = sortOption;
    }, [sortOption]);

    const agentFilterRef = useRef(agentFilter);
    const sourceFilterRef = useRef(sourceFilter);

    useEffect(() => {
        agentFilterRef.current = agentFilter;
    }, [agentFilter]);

    useEffect(() => {
        sourceFilterRef.current = sourceFilter;
    }, [sourceFilter]);

    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const response = await get('/agents/');
                if (response?.data && Array.isArray(response.data)) {
                    setAllAgents(response.data.map((agent: any) => ({
                        id: agent.id,
                        parent_agent_id: agent.parent_agent_id,
                        name: agent.name || agent.id,
                    })));
                }
            } catch (error) {
                console.error('Error fetching agents:', error);
            }
        };
        fetchAgents();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const currentPage = parseInt(params.get('page') || '1');
        const currentSearch = params.get('search') || '';
        const urlTimeFilter = params.get('time_filter') || 'last_90_days';
        const urlSort = params.get('sort') || 'last_desc';
        const urlAgentFilter = params.get('agent_id') || 'all';
        const urlSourceFilter = params.get('conversation_type') || 'all';

        const validTimeFilters = new Set([
            'today',
            'yesterday',
            'last_3_days',
            'this_week',
            'this_month',
            'last_90_days',
            'last_year',
        ]);
        const validSortOptions = new Set(['created_desc', 'created_asc', 'last_desc', 'last_asc']);

        const normalizedTimeFilter = validTimeFilters.has(urlTimeFilter) ? urlTimeFilter : 'last_90_days';
        const normalizedSort = validSortOptions.has(urlSort) ? urlSort : 'last_desc';

        setPage(currentPage);
        setSearchInput(currentSearch);
        setTimeFilter(normalizedTimeFilter);
        setSortOption(normalizedSort);
        setAgentFilter(urlAgentFilter);
        setSourceFilter(urlSourceFilter);
        getSessions(currentPage, currentSearch, normalizedTimeFilter, normalizedSort, urlAgentFilter, urlSourceFilter);
    }, [location.search]);

    const filterSessionsByTime = (sessions: any[], timeFilter: string) => {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfTomorrow = new Date(startOfToday);
        startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
        const startOfYesterday = new Date(startOfToday);
        startOfYesterday.setDate(startOfYesterday.getDate() - 1);

        return sessions.filter((session) => {
            const sessionDate = parseApiDate(session.created_at);
            if (!sessionDate) {
                return false;
            }

            switch (timeFilter) {
                case 'today':
                    return sessionDate >= startOfToday && sessionDate < startOfTomorrow;
                case 'yesterday':
                    return sessionDate >= startOfYesterday && sessionDate < startOfToday;
                case 'last_3_days':
                    // Includes today + previous 2 days
                    const threeDaysAgo = new Date(startOfToday);
                    threeDaysAgo.setDate(threeDaysAgo.getDate() - 2);
                    return sessionDate >= threeDaysAgo;
                case 'this_week':
                    // Includes today + previous 6 days
                    const weekAgo = new Date(startOfToday);
                    weekAgo.setDate(weekAgo.getDate() - 6);
                    return sessionDate >= weekAgo;
                case 'this_month':
                    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                    return sessionDate >= monthStart;
                case 'last_90_days':
                    // Includes today + previous 89 days
                    const ninetyDaysAgo = new Date(startOfToday);
                    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 89);
                    return sessionDate >= ninetyDaysAgo;
                case 'last_year':
                    // Includes today + previous 364 days
                    const yearAgo = new Date(startOfToday);
                    yearAgo.setDate(yearAgo.getDate() - 364);
                    return sessionDate >= yearAgo;
                default:
                    return true;
            }
        });
    };

    const sortSessions = (sessionsToSort: any[], sort: string) => {
        const getTimeMs = (value: any): number => {
            if (!value) return 0;
            const parsedDate = parseApiDate(value);
            if (!parsedDate) return 0;
            const ms = parsedDate.getTime();
            return Number.isNaN(ms) ? 0 : ms;
        };

        const sortKey = sort.startsWith('last_') ? 'last_activity' : 'created_at';
        const direction = sort.endsWith('_asc') ? 'asc' : 'desc';
        const dirMultiplier = direction === 'asc' ? 1 : -1;

        return [...sessionsToSort].sort((a, b) => {
            const aValue = sortKey === 'last_activity' ? (a.last_activity || a.created_at) : a.created_at;
            const bValue = sortKey === 'last_activity' ? (b.last_activity || b.created_at) : b.created_at;
            return (getTimeMs(aValue) - getTimeMs(bValue)) * dirMultiplier;
        });
    };

    const getSessions = async (
        page: number = 1,
        search: string = '',
        timeFilterValue: string = timeFilter,
        sortValue: string = sortOption,
        agentFilterValue: string = agentFilter,
        sourceFilterValue: string = sourceFilter,
        options: GetSessionsOptions = {}
    ) => {
        const shouldShowLoader = !options.silent;
        try {
            if (shouldShowLoader) {
                setLoading(true);
            }
            const params = new URLSearchParams({
                page: page.toString(),
                per_page: '100',
                search,
            });

            const selectedTab = localStorage.getItem('selectedTab');
            if (selectedTab && typeMap[selectedTab]) {
                params.set('agent_type', typeMap[selectedTab]);
            }

            if (agentFilterValue && agentFilterValue !== 'all') {
                params.set('agent_id', agentFilterValue);
            }

            if (sourceFilterValue && sourceFilterValue !== 'all') {
                params.set('conversation_type', sourceFilterValue);
            }

            const data = await get(`/sessions/?${params.toString()}`);
            const filteredSessions = filterSessionsByTime(data.data.items, timeFilterValue);
            setSessions(sortSessions(filteredSessions, sortValue));
            setPage(page);
            setTotalPages(Math.ceil(filteredSessions.length / 25));
            captureEvent('viewed_sessions', {});
        } catch (error) {
            console.error('Error fetching sessions:', error);
        } finally {
            if (shouldShowLoader) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        getSessionsRef.current = getSessions;
    }, [getSessions]);

    const scheduleRealtimeSessionsRefresh = useCallback(() => {
        if (realtimeRefreshTimerRef.current) {
            clearTimeout(realtimeRefreshTimerRef.current);
        }
        realtimeRefreshTimerRef.current = setTimeout(() => {
            void getSessionsRef.current(
                pageRef.current,
                searchInputRef.current,
                timeFilterRef.current,
                sortOptionRef.current,
                agentFilterRef.current,
                sourceFilterRef.current,
                { silent: true }
            );
        }, 350);
    }, []);

    const handleSearch = (search: string) => {
        const params = new URLSearchParams(location.search);
        if (search) {
            params.set('search', search);
        } else {
            params.delete('search');
        }
        params.set('page', '1');
        navigate(`?${params.toString()}`);
    };

    useEffect(() => {
        if (selectedSessionId && selectedSessionStatus) {
            setSessions(prevSessions =>
                prevSessions.map(session =>
                    session.id === selectedSessionId && session.status !== selectedSessionStatus
                        ? { ...session, status: selectedSessionStatus }
                        : session
                )
            );
        }
    }, [selectedSessionId, selectedSessionStatus]);

    useEffect(() => {
        if (!orgEvent?.type) {
            return;
        }
        if (orgEvent.type === 'session_active' || orgEvent.type === 'session_inactive') {
            scheduleRealtimeSessionsRefresh();
        }
    }, [orgEvent, scheduleRealtimeSessionsRefresh]);

    useEffect(() => {
        return () => {
            if (realtimeRefreshTimerRef.current) {
                clearTimeout(realtimeRefreshTimerRef.current);
                realtimeRefreshTimerRef.current = null;
            }
        };
    }, []);

    const formatSessionListDate = (value?: string | null) => {
        const date = parseApiDate(value);
        if (!date) return '';

        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        if (isToday) {
            return new Intl.DateTimeFormat('en-US', {
                hour: 'numeric',
                minute: '2-digit',
            }).format(date);
        }

        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        if (date.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        }

        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
        }).format(date);
    };

    return (
        <div className={`flex flex-col w-full md:w-1/3 md:min-w-[250px] min-w-0 border-r max-h-screen overflow-y-auto overflow-x-hidden scrollbar-hidden`}>
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-border dark:border-gray-700">
                <div className="flex items-center gap-2">
                    {onNavigateHome && (
                        <button onClick={onNavigateHome} className="md:hidden p-1 -ml-1 text-muted-foreground hover:text-foreground">
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                    )}
                    <h1 className="text-xl font-semibold dark:text-slate-100">Sessions</h1>
                </div>
                <div className="text-sm text-muted-foreground">
                    {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'}
                </div>
            </div>
            <div className={`w-full grow pt-4`}>
                <div className="mx-4 rounded-md">
                    <div className="flex flex-col gap-2 w-full">
                        <div className="flex gap-2">
                            <Input
                                type="text"
                                id="search"
                                placeholder="Search sessions by contact..."
                                autoComplete='off'
                                className="flex-1"
                                value={searchInput}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                    if (e.key === 'Enter') {
                                        handleSearch(e.currentTarget.value);
                                    }
                                }}
                            />
                            <Popover open={filterPopoverOpen} onOpenChange={setFilterPopoverOpen}>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" size="icon" className="h-9 w-9 shrink-0 relative">
                                        <SlidersHorizontal className="h-4 w-4" />
                                        {(timeFilter !== 'last_90_days' || sortOption !== 'last_desc' || agentFilter !== 'all' || sourceFilter !== 'all') && (
                                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full" />
                                        )}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-64" align="end">
                                    <div className="grid gap-3">
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-muted-foreground">Date Range</label>
                                            <Select
                                                value={timeFilter}
                                                onValueChange={(value) => {
                                                    setTimeFilter(value);
                                                    const params = new URLSearchParams(location.search);
                                                    params.set('time_filter', value);
                                                    params.set('page', '1');
                                                    navigate(`?${params.toString()}`);
                                                }}
                                            >
                                                <SelectTrigger className="h-9">
                                                    <SelectValue placeholder="Date" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="today">Today</SelectItem>
                                                    <SelectItem value="yesterday">Yesterday</SelectItem>
                                                    <SelectItem value="last_3_days">Last 3 days</SelectItem>
                                                    <SelectItem value="this_week">This past week</SelectItem>
                                                    <SelectItem value="this_month">This month</SelectItem>
                                                    <SelectItem value="last_90_days">Last 90 days</SelectItem>
                                                    <SelectItem value="last_year">Last year</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-muted-foreground">Sort By</label>
                                            <Select
                                                value={sortOption}
                                                onValueChange={(value) => {
                                                    setSortOption(value);
                                                    setSessions(prev => sortSessions(prev, value));
                                                    const params = new URLSearchParams(location.search);
                                                    params.set('sort', value);
                                                    params.set('page', '1');
                                                    navigate(`?${params.toString()}`);
                                                }}
                                            >
                                                <SelectTrigger className="h-9">
                                                    <SelectValue placeholder="Sort" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="last_desc">Active (newest)</SelectItem>
                                                    <SelectItem value="last_asc">Active (oldest)</SelectItem>
                                                    <SelectItem value="created_desc">Started (newest)</SelectItem>
                                                    <SelectItem value="created_asc">Started (oldest)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-muted-foreground">Agent</label>
                                            <Select
                                                value={agentFilter}
                                                onValueChange={(value) => {
                                                    setAgentFilter(value);
                                                    const params = new URLSearchParams(location.search);
                                                    if (value !== 'all') {
                                                        params.set('agent_id', value);
                                                    } else {
                                                        params.delete('agent_id');
                                                    }
                                                    params.set('page', '1');
                                                    navigate(`?${params.toString()}`);
                                                }}
                                            >
                                                <SelectTrigger className="h-9">
                                                    <SelectValue placeholder="All Agents" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">All Agents</SelectItem>
                                                    {allAgents.map((agent) => (
                                                        <SelectItem key={agent.id} value={agent.parent_agent_id}>
                                                            {agent.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-muted-foreground">Source</label>
                                            <Select
                                                value={sourceFilter}
                                                onValueChange={(value) => {
                                                    setSourceFilter(value);
                                                    const params = new URLSearchParams(location.search);
                                                    if (value !== 'all') {
                                                        params.set('conversation_type', value);
                                                    } else {
                                                        params.delete('conversation_type');
                                                    }
                                                    params.set('page', '1');
                                                    navigate(`?${params.toString()}`);
                                                }}
                                            >
                                                <SelectTrigger className="h-9">
                                                    <SelectValue placeholder="All Sources" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">All Sources</SelectItem>
                                                    <SelectItem value="lead">
                                                        <span className="flex items-center gap-2"><Globe className="h-3.5 w-3.5 text-green-500" /> Website</span>
                                                    </SelectItem>
                                                    <SelectItem value="app">
                                                        <span className="flex items-center gap-2"><Monitor className="h-3.5 w-3.5 text-blue-500" /> App</span>
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </PopoverContent>
                            </Popover>
                        </div>
                        {/* Active filter labels */}
                        {(timeFilter !== 'last_90_days' || sortOption !== 'last_desc' || agentFilter !== 'all' || sourceFilter !== 'all') && (
                            <div className="flex flex-wrap gap-1.5">
                                {timeFilter !== 'last_90_days' && (
                                    <Badge variant="secondary" className="text-xs font-normal gap-1 pr-1">
                                        {{
                                            today: 'Today',
                                            yesterday: 'Yesterday',
                                            last_3_days: 'Last 3 days',
                                            this_week: 'This week',
                                            this_month: 'This month',
                                            last_year: 'Last year',
                                        }[timeFilter] || timeFilter}
                                        <button
                                            onClick={() => {
                                                setTimeFilter('last_90_days');
                                                const params = new URLSearchParams(location.search);
                                                params.delete('time_filter');
                                                params.set('page', '1');
                                                navigate(`?${params.toString()}`);
                                            }}
                                            className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </Badge>
                                )}
                                {sortOption !== 'last_desc' && (
                                    <Badge variant="secondary" className="text-xs font-normal gap-1 pr-1">
                                        {{
                                            last_asc: 'Active (oldest)',
                                            created_desc: 'Started (newest)',
                                            created_asc: 'Started (oldest)',
                                        }[sortOption] || sortOption}
                                        <button
                                            onClick={() => {
                                                setSortOption('last_desc');
                                                setSessions(prev => sortSessions(prev, 'last_desc'));
                                                const params = new URLSearchParams(location.search);
                                                params.delete('sort');
                                                params.set('page', '1');
                                                navigate(`?${params.toString()}`);
                                            }}
                                            className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </Badge>
                                )}
                                {agentFilter !== 'all' && (
                                    <Badge variant="secondary" className="text-xs font-normal gap-1 pr-1">
                                        {allAgents.find(a => a.parent_agent_id === agentFilter)?.name || 'Agent'}
                                        <button
                                            onClick={() => {
                                                setAgentFilter('all');
                                                const params = new URLSearchParams(location.search);
                                                params.delete('agent_id');
                                                params.set('page', '1');
                                                navigate(`?${params.toString()}`);
                                            }}
                                            className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </Badge>
                                )}
                                {sourceFilter !== 'all' && (
                                    <Badge variant="secondary" className="text-xs font-normal gap-1 pr-1">
                                        {sourceFilter === 'lead' ? 'Website' : sourceFilter === 'app' ? 'App' : sourceFilter}
                                        <button
                                            onClick={() => {
                                                setSourceFilter('all');
                                                const params = new URLSearchParams(location.search);
                                                params.delete('conversation_type');
                                                params.set('page', '1');
                                                navigate(`?${params.toString()}`);
                                            }}
                                            className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </Badge>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <Separator className="mt-4" />
                <div className="h-full overflow-y-auto scrollbar-hidden pb-12">
                    {sessions.length > 0 && !loading && (
                        <div className="flex flex-col">
                            {sessions.map((session: any) => {
                                const identityStatus = resolveIdentityStatus({
                                    lead: session.lead,
                                    person: session.visitor_profile?.person,
                                    company: session.visitor_profile?.company,
                                });
                                const isSelected = selectedSessionId === session.id;
                                const sessionDate = session.created_at;
                                const source = session.channel === 'email'
                                    ? { label: 'Email', icon: Mail, color: 'text-amber-500' }
                                    : session.type === 'app'
                                        ? { label: 'App', icon: Monitor, color: 'text-blue-500' }
                                        : { label: 'Web', icon: Globe, color: 'text-emerald-500' };
                                const initiator = session.initiated_by === 'ai'
                                    ? { label: 'AI started', icon: Sparkles, color: 'text-violet-500' }
                                    : session.initiated_by === 'human'
                                        ? { label: 'Rep started', icon: Headset, color: 'text-emerald-500' }
                                        : session.initiated_by === 'sdk'
                                            ? { label: 'SDK', icon: Code, color: 'text-orange-500' }
                                            : session.initiated_by === 'ai_agent'
                                                ? { label: 'AI agent', icon: Bot, color: 'text-cyan-500' }
                                                : null;
                                const SourceIcon = source.icon;
                                const InitiatorIcon = initiator?.icon;

                                return (
                                <button
                                    type="button"
                                    key={session.id}
                                    aria-current={isSelected ? 'true' : undefined}
                                    className={`group relative w-full border-b border-border/60 px-4 py-3 text-left transition-colors focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
                                        isSelected ? 'bg-primary/[0.07]' : 'hover:bg-muted/40'
                                    }`}
                                    onClick={() => {
                                        setSessionId(session.id);
                                        setSelectedSessionStatus(session.status);
                                        navigate({ pathname: `/sessions/${session.id}`, search: location.search }, { replace: true });
                                    }}
                                >
                                    {isSelected && (
                                        <span className="absolute inset-y-2 left-0 w-0.5 rounded-r-full bg-primary" />
                                    )}

                                    <div className="flex min-w-0 items-start gap-3">
                                        <div className="min-w-0 flex-1">
                                            <VisitorIdentitySummary
                                                status={identityStatus}
                                                lead={session.lead}
                                                person={session.visitor_profile?.person}
                                                company={session.visitor_profile?.company}
                                                verifiedTitle={getIdentityVerificationTitle({
                                                    leadIdentityVerifiedAt: session.lead?.identity_verified_at,
                                                    sessionIdentityVerifiedAt: session.visitor_profile?.identity_verified_at,
                                                })}
                                            />
                                        </div>
                                        <div className="flex shrink-0 items-center gap-2 pt-0.5">
                                            <ModerationBadge moderation={session.moderation} />
                                            {session.status === 'active' && (
                                                <span className="relative flex h-2 w-2" title="Active session">
                                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                                                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                                                    <span className="sr-only">Active session</span>
                                                </span>
                                            )}
                                            <time
                                                dateTime={sessionDate}
                                                title={formatDate(sessionDate, 'short')}
                                                className="whitespace-nowrap text-[11px] tabular-nums text-muted-foreground"
                                            >
                                                {formatSessionListDate(sessionDate)}
                                            </time>
                                        </div>
                                    </div>

                                    {session.channel === 'email' && session.name && (
                                        <div className="mt-1 truncate pl-[22px] text-xs text-muted-foreground">
                                            {session.name}
                                        </div>
                                    )}

                                    <div className="mt-2 flex min-w-0 items-center gap-2 pl-[22px] text-[11px] text-muted-foreground">
                                        <span className="inline-flex shrink-0 items-center gap-1">
                                            <SourceIcon className={`h-3 w-3 ${source.color}`} />
                                            {source.label}
                                        </span>
                                        {initiator && InitiatorIcon && (
                                            <>
                                                <span className="text-border" aria-hidden="true">•</span>
                                                <span className="inline-flex min-w-0 items-center gap-1">
                                                    <InitiatorIcon className={`h-3 w-3 shrink-0 ${initiator.color}`} />
                                                    <span className="truncate">{initiator.label}</span>
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </button>
                                );
                            })}
                        </div>
                    )}

                    {sessions.length === 0 && !loading && (
                        <div className="text-center py-16 text-slate-500 dark:text-slate-400">
                            No sessions
                        </div>
                    )}

                    {loading && (
                        <div className="text-center py-16">
                            <BeatLoader />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SessionsCard;
